﻿import { Observable, Subscription, Subject, BehaviorSubject } from 'common/barrels/rx';

export type WriteOnlyBinding<T> = (x: T) => void;

export type ReadOnlyBinding<T> = T | Observable<T>;

export type ValueBinding<T> = T | BehaviorSubject<T>;

export type Binding<T> = WriteOnlyBinding<T> |ReadOnlyBinding<T> | ValueBinding<T>;

export function canWriteOnly<T>(object: Binding<T>): object is WriteOnlyBinding<T> {
    return (object instanceof Function) || (object instanceof Subject);
}

export function canWrite<T>(object: Binding<T>): object is WriteOnlyBinding<T> | BehaviorSubject<T> {
    return (object instanceof Function) || (object instanceof Subject);
}

export function canRead<T>(object: Binding<T>): object is Observable<T> | T {
    return (object instanceof Observable) && !(object instanceof Function);
}

export function canRetrieve<T>(object: Binding<T>): object is ValueBinding<T> {
    return canReadWrite(object) || (typeof object !== 'function' && !(object instanceof Observable));
}

export function canReadOnly<T>(object: Binding<T>): object is T {
    return !(object instanceof Observable) && !(object instanceof Function);
}

export function canObserve<T>(object: Binding<T>): object is Observable<T> {
    return (object instanceof Observable);
}

export function canReadWrite<T>(object: Binding<T>): object is BehaviorSubject<T> {
    return object instanceof BehaviorSubject;
}

export function bind<T>(binding: Binding<T>, writeToControl: (x: T) => void, controlValues: () => Observable<T>): Subscription {
    const subscriptions = new Subscription;

    if (canObserve(binding)) {
        subscriptions.add(binding.subscribe(x => writeToControl(x)));
        if (canWrite(binding)) {
            subscriptions.add(controlValues().subscribe(x => binding.next(x)));
        }
    } else if (canWriteOnly(binding)) {
        subscriptions.add(controlValues().subscribe(x => binding(x)));
    } else {
        writeToControl(binding);
    }

    return subscriptions;
}
